/*
 * Silk & Hide: lightweight motion helpers.
 *
 * 1. Scroll reveal on the homepage: content below the fold fades/rises in once.
 *    Classes are only added by this script, so without JS everything is simply visible.
 * 2. Product card hover flag, so the second image cross-fades instead of snapping (see silk-hide.css).
 * 3. Gentle parallax on images marked [data-sh-parallax="speed"] (desktop pointers only).
 * 4. Header scroll state: html[data-sh-scrolled] once the page has moved (styling only, no layout change).
 *
 * Motion (1–3) is skipped when the visitor prefers reduced motion or inside the theme editor.
 */
(function () {
  /* ---------- Header scroll state (not motion, so always on) ---------- */

  const root = document.documentElement;
  let scrolledTicking = false;
  const updateScrolled = () => {
    root.toggleAttribute('data-sh-scrolled', window.scrollY > 8);
    scrolledTicking = false;
  };
  window.addEventListener(
    'scroll',
    () => {
      if (scrolledTicking) return;
      scrolledTicking = true;
      requestAnimationFrame(updateScrolled);
    },
    { passive: true }
  );
  updateScrolled();

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (window.Shopify && window.Shopify.designMode) return;

  /* ---------- Parallax ---------- */

  const parallaxItems = Array.from(document.querySelectorAll('[data-sh-parallax]'));
  if (parallaxItems.length && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    let parallaxTicking = false;
    const updateParallax = () => {
      const viewportMid = window.innerHeight / 2;
      for (const el of parallaxItems) {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > window.innerHeight + 200) continue;
        const speed = parseFloat(el.dataset.shParallax) || 0.05;
        const offset = (rect.top + rect.height / 2 - viewportMid) * speed;
        const img = el.querySelector('img');
        if (img) img.style.translate = `0 ${offset.toFixed(1)}px`;
      }
      parallaxTicking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (parallaxTicking) return;
        parallaxTicking = true;
        requestAnimationFrame(updateParallax);
      },
      { passive: true }
    );
    window.addEventListener('resize', updateParallax, { passive: true });
    updateParallax();
  }

  /* ---------- Product card hover flag ---------- */

  const HOVER_ATTR = 'data-sh-hover';
  const leaveTimers = new WeakMap();

  document.addEventListener(
    'pointerover',
    (event) => {
      if (event.pointerType !== 'mouse') return;
      const card = event.target instanceof Element && event.target.closest('product-card');
      if (!card) return;
      clearTimeout(leaveTimers.get(card));
      card.setAttribute(HOVER_ATTR, '');
    },
    { passive: true }
  );

  document.addEventListener(
    'pointerout',
    (event) => {
      if (event.pointerType !== 'mouse') return;
      const card = event.target instanceof Element && event.target.closest('product-card');
      if (!card || (event.relatedTarget instanceof Node && card.contains(event.relatedTarget))) return;
      // Keep the flag briefly so the image returning to the first slide also fades.
      leaveTimers.set(card, setTimeout(() => card.removeAttribute(HOVER_ATTR), 700));
    },
    { passive: true }
  );

  /* ---------- Homepage scroll reveal ---------- */

  const main = document.querySelector('main[data-template="index"]');
  if (!main || !('IntersectionObserver' in window)) return;

  const TARGETS = '[data-sh-reveal], .resource-list__item, .section-resource-list__header, .media-block, .text-block, a.size-style';
  const STAGGER_MS = 90;
  const MAX_STAGGER = 4;
  const FINISH_AFTER_MS = 1900; // longest settle in silk-hide.css is 1.6s, plus margin

  const sections = Array.from(main.querySelectorAll(':scope > .shopify-section')).slice(1); // never the hero
  const viewportBottom = window.innerHeight;

  const finish = (el) => {
    el.classList.remove('sh-reveal', 'sh-revealed');
    el.style.removeProperty('--sh-delay');
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target;
        observer.unobserve(el);
        el.classList.add('sh-revealed');
        // Return the element to its untouched state once the longest transition (image settle) has run.
        const delay = parseInt(el.style.getPropertyValue('--sh-delay'), 10) || 0;
        setTimeout(() => finish(el), delay + FINISH_AFTER_MS);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );

  for (const section of sections) {
    const candidates = Array.from(section.querySelectorAll(TARGETS));
    for (const el of candidates) {
      // Only ever reveal the outermost match (a card, never the title or price inside it),
      // even when that outer element was left visible because it started on screen.
      if (el.parentElement && el.parentElement.closest(TARGETS)) continue;
      // Anything already on screen stays put, so there is never a flash on load.
      if (el.getBoundingClientRect().top < viewportBottom) continue;
      // Carousel slides scroll in sideways; they must never be hidden waiting for a vertical reveal.
      if (el.closest('slideshow-component, slideshow-slides')) continue;
      // Collapsed accordion content (FAQ answers) has its own open animation; never hide it here.
      if (el.closest('details')) continue;

      const siblings = el.parentElement ? Array.from(el.parentElement.children) : [el];
      const index = Math.max(0, siblings.indexOf(el)) % MAX_STAGGER;
      el.style.setProperty('--sh-delay', `${index * STAGGER_MS}ms`);
      el.classList.add('sh-reveal');
      observer.observe(el);
    }
  }
})();
